import React, { useEffect, useState } from 'react';
import api from '../api';
import PageHeader from '../components/PageHeader';
import BotSelector from '../components/BotSelector';
import { Avatar, Ic, Spinner, StatCard, fmtNum, useToast } from '../components/ui';

const AUDIENCE_TYPES = [
  { value: 'inactive', label: 'İnaktif kullanıcılar', desc: 'Belirli süredir mesaj göndermeyenler', ic: 'moon', c: '#7f9cf5',
    fields: [{ key: 'hours', label: 'Kaç saattir inaktif?', type: 'number', default: 24, placeholder: '24' }] },
  { value: 'flow_abandoned', label: 'Flow terk edenler', desc: "Flow'u başlatıp tamamlamayanlar", ic: 'flow', c: '#ef5b5b', fields: [] },
  { value: 'flow_completed', label: 'Flow tamamlayanlar', desc: "Flow'u bitirenler", ic: 'checkCircle', c: '#56c26a', fields: [] },
  { value: 'no_flow', label: 'Flow başlatmayanlar', desc: 'Hiç flow başlatmamış kullanıcılar', ic: 'sparkle', c: '#4fd1c5', fields: [] },
  { value: 'new_users', label: 'Yeni kullanıcılar', desc: 'Son X gün içinde katılanlar', ic: 'userPlus', c: '#6ab3f3',
    fields: [{ key: 'days', label: 'Son kaç gün?', type: 'number', default: 7, placeholder: '7' }] },
  { value: 'low_xp', label: "Düşük XP'liler", desc: 'Az XP kazanmış kullanıcılar', ic: 'trendDown', c: '#e5a443',
    fields: [{ key: 'max_xp', label: 'Max XP', type: 'number', default: 50, placeholder: '50' }] },
  { value: 'high_xp', label: 'VIP kullanıcılar', desc: 'Yüksek XP kazananlar', ic: 'crown', c: '#f5c542',
    fields: [{ key: 'min_xp', label: 'Min XP', type: 'number', default: 500, placeholder: '500' }] },
  { value: 'level_range', label: 'Level aralığı', desc: 'Belirli level aralığındakiler', ic: 'level', c: '#a695e7',
    fields: [{ key: 'min_level', label: 'Min level', type: 'number', default: 1 }, { key: 'max_level', label: 'Max level', type: 'number', default: 5 }] },
  { value: 'streak_broken', label: 'Serisi bozulanlar', desc: 'Düzenli gelip sonra kesenler', ic: 'fire', c: '#ff8a4c',
    fields: [{ key: 'min_games', label: 'Min oyun sayısı', type: 'number', default: 3 }] },
  { value: 'tag_filter', label: 'Etiket filtresi', desc: 'Belirli etikete sahip kullanıcılar', ic: 'tag', c: '#f27eb5',
    fields: [{ key: 'tags', label: 'Etiketler (virgülle)', type: 'text', placeholder: 'VIP, Premium' }] },
  { value: 'all_users', label: 'Tüm kullanıcılar', desc: 'Botun tüm kullanıcıları', ic: 'users', c: '#6ab3f3', fields: [] },
];
const audMap = Object.fromEntries(AUDIENCE_TYPES.map(a => [a.value, a]));

const STATUS = {
  draft: { label: 'Taslak', cls: 'neutral', c: '#7f91a4' },
  active: { label: 'Aktif', cls: 'success', c: '#56c26a' },
  paused: { label: 'Duraklatıldı', cls: 'warning', c: '#e5a443' },
  completed: { label: 'Tamamlandı', cls: 'info', c: '#6ab3f3' },
};

const EMPTY_FORM = {
  name: '', description: '', audience_type: 'inactive', audience_config: { hours: 24 },
  message_content: '', message_buttons: [], schedule_type: 'once', scheduled_at: '',
  recurring_interval: 24, recurring_unit: 'hours', max_sends_per_user: 1, frequency_cap_hours: 0, daily_limit: 0,
};

const CSS = `
.rt-aud { display:grid; grid-template-columns:repeat(auto-fill, minmax(min(100%, 230px), 1fr)); gap:8px; }
.rt-opt { display:flex; align-items:center; gap:10px; padding:9px 11px; border-radius:12px; cursor:pointer; border:1.5px solid var(--border3); background:var(--surface2); transition:border-color .12s, background .12s; min-width:0; text-align:left; color:inherit; font:inherit; width:100%; }
.rt-opt:hover { border-color:var(--border); }
.rt-opt.on { border-color:var(--accent); background:rgba(82,136,193,.14); }
.rt-ic { width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.rt-form { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:14px; align-items:start; }
.rt-col { display:flex; flex-direction:column; gap:14px; min-width:0; }
.rt-sec { display:flex; align-items:center; gap:8px; font-size:14px; font-weight:600; color:var(--link); margin-bottom:12px; }
.rt-row { display:flex; align-items:center; gap:14px; padding:14px 16px; border-bottom:1px solid var(--border3); min-width:0; position:relative; }
.rt-row:last-child { border-bottom:none; }
.rt-row:hover { background:var(--hover); }
.rt-stats { display:flex; gap:18px; flex-shrink:0; text-align:center; }
.rt-stats b { display:block; font-size:16px; font-weight:700; line-height:1.2; }
.rt-stats span { font-size:11.5px; color:var(--text3); }
.rt-acts { display:flex; gap:2px; flex-shrink:0; }
.rt-main { flex:1 1 0; }
.tg-prev { background:#0e1621 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Ccircle cx='10' cy='10' r='1.2' fill='%23ffffff' fill-opacity='.04'/%3E%3Ccircle cx='40' cy='35' r='1.2' fill='%23ffffff' fill-opacity='.04'/%3E%3C/svg%3E"); border-radius:12px; padding:16px 12px; }
.tg-bub { background:#182533; border-radius:15px 15px 15px 4px; padding:7px 10px 6px 11px; max-width:min(100%, 420px); font-size:14.5px; line-height:1.42; white-space:pre-wrap; word-break:break-word; box-shadow:0 1px 2px rgba(0,0,0,.18); }
.tg-kb { display:flex; flex-direction:column; gap:4px; margin-top:4px; max-width:min(100%, 420px); }
.tg-kb .b { background:rgba(46,63,82,.85); color:#fff; border-radius:8px; padding:8px 10px; font-size:14px; font-weight:500; text-align:center; position:relative; display:flex; align-items:center; justify-content:center; gap:6px; }
@media (max-width: 1000px) { .rt-form { grid-template-columns:minmax(0,1fr); } }
@media (max-width: 768px) {
  .rt-row { flex-wrap:wrap; padding:12px; gap:10px 12px; }
  .rt-row .rt-main { flex:1 1 calc(100% - 60px); }
  .rt-row .rt-main .truncate { white-space:normal; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
  .rt-stats { flex:0 1 auto; justify-content:flex-start; gap:14px; text-align:left; }
  .rt-acts { margin-left:auto; }
  .rt-acts .btn-icon { width:34px; min-height:34px; }
}
`;

function esc(s) { return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
function previewHtml(text) {
  let h = esc(text).replace(/\{\{\s*first_name\s*\}\}/g, 'Ahmet').replace(/\{\{\s*full_name\s*\}\}/g, 'Ahmet Yılmaz');
  h = h.replace(/&lt;(\/?)(b|strong|i|em|u|s|code)&gt;/gi, '<$1$2>');
  return h;
}

function TgPreview({ text, buttons }) {
  return (
    <div className="tg-prev">
      <div className="tg-bub">
        {text ? <span dangerouslySetInnerHTML={{ __html: previewHtml(text) }} /> : <span style={{ color: 'var(--text3)' }}>Mesaj önizlemesi burada görünür…</span>}
        <span style={{ float: 'right', margin: '6px 0 -3px 10px', fontSize: 12, color: '#6d7f8f' }}>12:00</span>
      </div>
      {buttons?.length > 0 && (
        <div className="tg-kb">
          {buttons.map((row, i) => (
            <div key={i} style={{ display: 'flex', gap: 4 }}>
              {(Array.isArray(row) ? row : [row]).map((b, j) => (
                <div key={j} className="b" style={{ flex: 1 }}>{b.text}{b.url && <Ic n="external" s={13} style={{ position: 'absolute', right: 6, top: 6 }} />}</div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function scheduleText(c) {
  return c.schedule_type === 'recurring' ? `Her ${c.recurring_interval} ${c.recurring_unit === 'hours' ? 'saatte' : 'günde'} bir` : 'Tek seferlik';
}

export default function Retargeting() {
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignLogs, setCampaignLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [running, setRunning] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [newBtn, setNewBtn] = useState({ text: '', url: '', callback: '' });
  const [toastEl, toast] = useToast();

  useEffect(() => {
    api.get('/bots').then(r => { setBots(r.data || []); if (r.data?.length) setSelectedBot(r.data[0]); }).catch(() => {});
  }, []);
  useEffect(() => { if (selectedBot) loadCampaigns(); }, [selectedBot]);

  async function loadCampaigns() {
    setLoading(true);
    try { const res = await api.get(`/retargeting/${selectedBot.id}`); setCampaigns(res.data || []); } catch (e) { setCampaigns([]); }
    setLoading(false);
  }

  async function loadDetail(campaign) {
    setSelectedCampaign(campaign);
    setCampaignLogs([]);
    setView('detail');
    setLogsLoading(true);
    try { const res = await api.get(`/retargeting/${selectedBot.id}/${campaign.id}/logs`); setCampaignLogs(res.data?.logs || []); } catch (e) { /* noop */ }
    setLogsLoading(false);
  }

  const updForm = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updConfig = (k, v) => setForm(f => ({ ...f, audience_config: { ...f.audience_config, [k]: v } }));

  function openCreate(campaign = null) {
    if (campaign) {
      setForm({
        name: campaign.name, description: campaign.description || '',
        audience_type: campaign.audience_type, audience_config: campaign.audience_config || {},
        message_content: campaign.message_content, message_buttons: campaign.message_buttons || [],
        schedule_type: campaign.schedule_type || 'once', scheduled_at: campaign.scheduled_at ? campaign.scheduled_at.slice(0, 16) : '',
        recurring_interval: campaign.recurring_interval || 24, recurring_unit: campaign.recurring_unit || 'hours',
        max_sends_per_user: campaign.max_sends_per_user || 1, frequency_cap_hours: campaign.frequency_cap_hours || 0, daily_limit: campaign.daily_limit || 0,
      });
      setSelectedCampaign(campaign);
    } else {
      setForm({ ...EMPTY_FORM, audience_config: { hours: 24 } });
      setSelectedCampaign(null);
    }
    setPreviewResult(null);
    setNewBtn({ text: '', url: '', callback: '' });
    setView('create');
    window.scrollTo(0, 0);
  }

  async function previewAudience() {
    setPreviewing(true);
    try {
      const res = await api.post(`/retargeting/${selectedBot.id}/preview-audience`, { audience_type: form.audience_type, audience_config: form.audience_config });
      setPreviewResult(res.data);
    } catch (e) { toast('Önizleme hatası', 'err'); }
    setPreviewing(false);
  }

  async function saveCampaign(activate = false) {
    if (!form.name || !form.message_content) { toast('Kampanya adı ve mesaj zorunlu', 'err'); return; }
    setSaving(true);
    try {
      const res = selectedCampaign
        ? await api.put(`/retargeting/${selectedBot.id}/${selectedCampaign.id}`, form)
        : await api.post(`/retargeting/${selectedBot.id}`, form);
      if (activate) await api.patch(`/retargeting/${selectedBot.id}/${res.data.id}/status`, { status: 'active' });
      toast(activate ? 'Kampanya aktifleştirildi' : 'Kaydedildi');
      loadCampaigns();
      setView('list');
    } catch (e) { toast(e.response?.data?.error || e.message, 'err'); }
    setSaving(false);
  }

  async function toggleStatus(camp) {
    const newStatus = camp.status === 'active' ? 'paused' : 'active';
    try {
      await api.patch(`/retargeting/${selectedBot.id}/${camp.id}/status`, { status: newStatus });
      toast(newStatus === 'active' ? 'Aktifleştirildi' : 'Duraklatıldı');
      loadCampaigns();
      if (selectedCampaign?.id === camp.id) setSelectedCampaign({ ...camp, status: newStatus });
    } catch (e) { toast('Durum değiştirilemedi', 'err'); }
  }

  async function runNow(camp) {
    setRunning(camp.id);
    try {
      const res = await api.post(`/retargeting/${selectedBot.id}/${camp.id}/run`);
      toast(`Gönderildi: ${fmtNum(res.data?.sent)} kullanıcı`);
      loadCampaigns();
      if (view === 'detail') loadDetail(camp);
    } catch (e) { toast(e.response?.data?.error || e.message, 'err'); }
    setRunning(null);
  }

  async function deleteCampaign(camp) {
    if (!window.confirm(`"${camp.name}" silinsin mi?`)) return;
    try { await api.delete(`/retargeting/${selectedBot.id}/${camp.id}`); toast('Silindi'); loadCampaigns(); if (view === 'detail') setView('list'); } catch (e) { toast('Silinemedi', 'err'); }
  }

  function addButton() {
    if (!newBtn.text) return;
    const btn = newBtn.url ? { text: newBtn.text, url: newBtn.url } : { text: newBtn.text, callback: newBtn.callback || newBtn.text };
    setForm(f => ({ ...f, message_buttons: [...f.message_buttons, [btn]] }));
    setNewBtn({ text: '', url: '', callback: '' });
  }

  const audienceInfo = audMap[form.audience_type];
  const totalStats = campaigns.reduce((acc, c) => ({
    sent: acc.sent + (parseInt(c.sent_count, 10) || 0),
    failed: acc.failed + (parseInt(c.failed_count, 10) || 0),
    active: acc.active + (c.status === 'active' ? 1 : 0),
    total: acc.total + 1,
  }), { sent: 0, failed: 0, active: 0, total: 0 });
  const ctrOf = c => ((parseInt(c.sent_count, 10) || 0) > 0 ? (((parseInt(c.clicked_count, 10) || 0) / parseInt(c.sent_count, 10)) * 100).toFixed(1) : '0');
  const botSel = <BotSelector bots={bots} selected={selectedBot} onSelect={b => { setSelectedBot(b); setView('list'); }} />;

  /* ═══ DETAY ═══ */
  if (view === 'detail' && selectedCampaign) {
    const camp = campaigns.find(c => c.id === selectedCampaign.id) || selectedCampaign;
    const aud = audMap[camp.audience_type] || { ic: 'target', c: '#6ab3f3', label: camp.audience_type };
    const st = STATUS[camp.status] || STATUS.draft;
    return (
      <div className="page-enter">
        <style>{CSS}</style>{toastEl}
        <button className="btn btn-ghost btn-sm" onClick={() => setView('list')} style={{ marginBottom: 14 }}><Ic n="back" s={16} />Kampanyalar</button>
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="flex-center gap-12" style={{ flexWrap: 'wrap' }}>
            <div className="rt-ic" style={{ width: 52, height: 52, background: aud.c + '22', color: aud.c }}><Ic n={aud.ic} s={26} /></div>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div className="flex-center gap-8" style={{ flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: 20 }} className="truncate">{camp.name}</h1>
                <span className={`badge badge-${st.cls}`}>{st.label}</span>
              </div>
              <div className="text-muted" style={{ fontSize: 13.5 }}>{aud.label} · {scheduleText(camp)}{camp.description ? ' · ' + camp.description : ''}</div>
            </div>
            <div className="flex gap-8" style={{ flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => openCreate(camp)}><Ic n="edit" s={16} />Düzenle</button>
              <button className="btn btn-ghost btn-sm" onClick={() => toggleStatus(camp)}><Ic n={camp.status === 'active' ? 'pause' : 'play'} s={15} />{camp.status === 'active' ? 'Duraklat' : 'Aktifleştir'}</button>
              <button className="btn btn-primary btn-sm" onClick={() => runNow(camp)} disabled={running === camp.id}>{running === camp.id ? <Spinner size={16} /> : <Ic n="send" s={16} />}Şimdi çalıştır</button>
            </div>
          </div>
        </div>

        <div className="stats-grid" style={{ marginBottom: 14 }}>
          <StatCard icon="send" label="Gönderim" value={fmtNum(camp.sent_count)} />
          <StatCard icon="alert" label="Başarısız" value={fmtNum(camp.failed_count)} color="var(--danger)" bg="var(--danger-s)" />
          <StatCard icon="click" label="Tıklama oranı" value={`%${ctrOf(camp)}`} color="var(--success)" bg="var(--success-s)" sub={`${fmtNum(camp.clicked_count)} tıklama`} />
          <StatCard icon="clock" label="Son çalışma" value={camp.last_run_at ? new Date(camp.last_run_at).toLocaleDateString('tr-TR') : '—'} color="var(--warning)" bg="var(--warning-s)"
            sub={camp.next_run_at && camp.status === 'active' ? `Sonraki: ${new Date(camp.next_run_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}` : null} />
        </div>

        <div className="rt-form">
          <div className="card">
            <div className="rt-sec"><Ic n="chat" s={18} />Kampanya mesajı</div>
            <TgPreview text={camp.message_content} buttons={camp.message_buttons} />
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="sec-title"><span className="flex-center gap-8"><Ic n="history" s={18} c="var(--link)" />Gönderim kaydı</span><span className="text-muted text-sm" style={{ fontWeight: 500 }}>{campaignLogs.length} kayıt</span></div>
            {logsLoading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner /></div>
              : !campaignLogs.length ? <div className="empty-state"><div className="icon"><Ic n="send" s={40} /></div><p>Henüz gönderim yok</p></div>
                : (
                  <div className="list" style={{ maxHeight: 520, overflowY: 'auto' }}>
                    {campaignLogs.map(log => {
                      const ok = log.status === 'sent', bad = log.status === 'failed';
                      return (
                        <div key={log.id} className="list-row" style={{ alignItems: 'flex-start' }}>
                          <Avatar id={log.telegram_id} name={log.full_name || '?'} size={38} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="flex-between" style={{ gap: 8 }}>
                              <span className="truncate" style={{ fontWeight: 600 }}>{log.full_name || log.telegram_id}</span>
                              <span className={`badge badge-${ok ? 'success' : bad ? 'danger' : 'info'}`}>{ok ? 'Gönderildi' : bad ? 'Hata' : log.status}</span>
                            </div>
                            <div className="text-muted" style={{ fontSize: 12.5 }}>
                              {new Date(log.sent_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}
                              {log.clicked_at && <span style={{ color: 'var(--success)' }}> · tıkladı {new Date(log.clicked_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>}
                            </div>
                            {log.error_message && <div style={{ fontSize: 12.5, color: 'var(--danger)', wordBreak: 'break-word', marginTop: 2 }}>{log.error_message}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
          </div>
        </div>
      </div>
    );
  }

  /* ═══ OLUŞTUR / DÜZENLE ═══ */
  if (view === 'create') {
    return (
      <div className="page-enter">
        <style>{CSS}</style>{toastEl}
        <button className="btn btn-ghost btn-sm" onClick={() => setView('list')} style={{ marginBottom: 14 }}><Ic n="back" s={16} />Kampanyalar</button>
        <PageHeader title={selectedCampaign ? 'Kampanyayı düzenle' : 'Yeni kampanya'} subtitle={selectedBot ? `${selectedBot.name} için hedefli mesaj` : ''} />

        <div className="rt-form">
          <div className="rt-col">
            <div className="card">
              <div className="rt-sec"><Ic n="clipboard" s={18} />Temel bilgiler</div>
              <div className="input-group"><label>Kampanya adı *</label><input className="input" value={form.name} onChange={e => updForm('name', e.target.value)} placeholder="Örn: 3 gün inaktif kullanıcılar" /></div>
              <div className="input-group" style={{ marginBottom: 0 }}><label>Açıklama</label><input className="input" value={form.description} onChange={e => updForm('description', e.target.value)} placeholder="Kampanya hakkında notlar…" /></div>
            </div>

            <div className="card">
              <div className="rt-sec"><Ic n="users" s={18} />Hedef kitle</div>
              <div className="rt-aud" style={{ marginBottom: 12 }}>
                {AUDIENCE_TYPES.map(a => {
                  const on = form.audience_type === a.value;
                  return (
                    <button type="button" key={a.value} className={'rt-opt' + (on ? ' on' : '')}
                      onClick={() => { setForm(f => ({ ...f, audience_type: a.value, audience_config: {} })); setPreviewResult(null); }}>
                      <div className="rt-ic" style={{ background: a.c + '22', color: a.c }}><Ic n={a.ic} s={18} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="truncate" style={{ fontSize: 14, fontWeight: 600 }}>{a.label}</div>
                        <div className="truncate" style={{ fontSize: 12, color: 'var(--text3)' }}>{a.desc}</div>
                      </div>
                      {on && <Ic n="checkCircle" s={18} c="var(--link)" />}
                    </button>
                  );
                })}
              </div>

              {audienceInfo?.fields?.length > 0 && (
                <div style={{ background: 'var(--accent-s2)', border: '1px solid var(--accent-s)', borderRadius: 12, padding: '12px 12px 0', marginBottom: 12 }}>
                  <div className="form-2">
                    {audienceInfo.fields.map(field => (
                      <div key={field.key} className="input-group">
                        <label>{field.label}</label>
                        <input className="input" type={field.type || 'text'} value={form.audience_config[field.key] ?? field.default ?? ''} placeholder={field.placeholder || ''}
                          onChange={e => updConfig(field.key, field.type === 'number' ? parseInt(e.target.value, 10) || 0 : e.target.value)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button className="btn btn-teal w-full" onClick={previewAudience} disabled={previewing}>
                {previewing ? <Spinner size={16} /> : <Ic n="eye" s={17} />}{previewing ? 'Hesaplanıyor…' : 'Hedef kitleyi önizle'}
              </button>
              {previewResult && (
                <div style={{ marginTop: 10, background: 'var(--success-s)', borderRadius: 12, padding: 12 }}>
                  <div className="flex-center gap-8" style={{ fontWeight: 700, color: 'var(--success)', marginBottom: previewResult.sample?.length ? 8 : 0 }}>
                    <Ic n="target" s={18} />{fmtNum(previewResult.count)} kullanıcı bu kriterlere uyuyor
                  </div>
                  {previewResult.sample?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {previewResult.sample.map((u, i) => (
                        <span key={i} className="chip" style={{ background: 'var(--surface3)', color: 'var(--text2)', paddingLeft: 3 }}>
                          <Avatar id={u.telegram_id} name={u.full_name || '?'} size={18} />{u.full_name || u.telegram_id}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="card">
              <div className="rt-sec"><Ic n="clock" s={18} />Zamanlama</div>
              <div className="seg" style={{ marginBottom: 12 }}>
                <button className={form.schedule_type === 'once' ? 'on' : ''} onClick={() => updForm('schedule_type', 'once')}>Tek seferlik</button>
                <button className={form.schedule_type === 'recurring' ? 'on' : ''} onClick={() => updForm('schedule_type', 'recurring')}>Tekrarlayan</button>
              </div>
              {form.schedule_type === 'once' ? (
                <div className="input-group" style={{ marginBottom: 0 }}>
                  <label>Gönderim tarihi ve saati</label>
                  <input type="datetime-local" className="input" value={form.scheduled_at} onChange={e => updForm('scheduled_at', e.target.value)} />
                  <div className="text-muted text-xs" style={{ marginTop: 4 }}>Boş bırakılırsa "Şimdi çalıştır" ile elle gönderilir.</div>
                </div>
              ) : (
                <div className="form-2">
                  <div className="input-group" style={{ marginBottom: 0 }}>
                    <label>Her kaç {form.recurring_unit === 'hours' ? 'saatte' : 'günde'} bir?</label>
                    <input type="number" className="input" min="1" value={form.recurring_interval} onChange={e => updForm('recurring_interval', parseInt(e.target.value, 10) || 1)} />
                  </div>
                  <div className="input-group" style={{ marginBottom: 0 }}>
                    <label>Birim</label>
                    <select className="input" value={form.recurring_unit} onChange={e => updForm('recurring_unit', e.target.value)}><option value="hours">Saat</option><option value="days">Gün</option></select>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="rt-col">
            <div className="card">
              <div className="rt-sec"><Ic n="chat" s={18} />Mesaj</div>
              <div className="input-group">
                <label>Mesaj * <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(HTML destekli)</span></label>
                <textarea className="input" value={form.message_content} onChange={e => updForm('message_content', e.target.value)}
                  placeholder={'Merhaba {{first_name}}!\n\nSeni bir süredir göremedik. Geri dön, seni bekliyoruz!'} style={{ minHeight: 130 }} />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {['{{first_name}}', '{{full_name}}'].map(v => (
                    <button key={v} type="button" className="btn btn-xs btn-teal" onClick={() => updForm('message_content', (form.message_content || '') + v)}><Ic n="plus" s={12} />{v}</button>
                  ))}
                  {[['<b>', '</b>', 'Kalın'], ['<i>', '</i>', 'İtalik']].map(([a, b, l]) => (
                    <button key={l} type="button" className="btn btn-xs btn-ghost" onClick={() => updForm('message_content', (form.message_content || '') + a + b)}>{l}</button>
                  ))}
                </div>
              </div>

              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--link)', marginBottom: 6 }}>Butonlar (opsiyonel)</label>
              {form.message_buttons.map((row, i) => (
                <div key={i} className="flex-center gap-8" style={{ background: 'var(--surface3)', borderRadius: 10, padding: '6px 6px 6px 12px', marginBottom: 6 }}>
                  <Ic n={(Array.isArray(row) ? row[0] : row)?.url ? 'link' : 'bolt'} s={15} c="var(--text3)" />
                  <span className="truncate" style={{ flex: 1, fontSize: 14 }}>{(Array.isArray(row) ? row : [row]).map(b => b.text).join(' · ')}</span>
                  <span className="truncate text-muted mono" style={{ fontSize: 12, maxWidth: '40%' }}>{(Array.isArray(row) ? row[0] : row)?.url || (Array.isArray(row) ? row[0] : row)?.callback}</span>
                  <button className="btn btn-icon" style={{ width: 28, minHeight: 28, color: 'var(--danger)' }} onClick={() => setForm(f => ({ ...f, message_buttons: f.message_buttons.filter((_, idx) => idx !== i) }))}><Ic n="close" s={15} /></button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <input className="input" style={{ flex: '1 1 140px' }} placeholder="Buton metni" value={newBtn.text} onChange={e => setNewBtn(b => ({ ...b, text: e.target.value }))} />
                <input className="input" style={{ flex: '1 1 160px' }} placeholder="URL veya callback" value={newBtn.url || newBtn.callback}
                  onChange={e => { const v = e.target.value; setNewBtn(b => (v.startsWith('http') ? { ...b, url: v, callback: '' } : { ...b, callback: v, url: '' })); }}
                  onKeyDown={e => e.key === 'Enter' && addButton()} />
                <button className="btn btn-teal" onClick={addButton} disabled={!newBtn.text}><Ic n="plus" s={16} />Ekle</button>
              </div>

              <div className="divider" style={{ margin: '16px 0 12px' }} />
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text3)', marginBottom: 8 }}>Önizleme</div>
              <TgPreview text={form.message_content} buttons={form.message_buttons} />
            </div>

            <div className="card">
              <div className="rt-sec"><Ic n="shield" s={18} />Limit ve güvenlik</div>
              <div className="form-3">
                <div className="input-group">
                  <label>Kişi başı max</label>
                  <input type="number" className="input" min="1" value={form.max_sends_per_user} onChange={e => updForm('max_sends_per_user', parseInt(e.target.value, 10) || 1)} />
                  <div className="text-muted text-xs" style={{ marginTop: 3 }}>Aynı kişiye en fazla</div>
                </div>
                <div className="input-group">
                  <label>Aralık (saat)</label>
                  <input type="number" className="input" min="0" value={form.frequency_cap_hours} onChange={e => updForm('frequency_cap_hours', parseInt(e.target.value, 10) || 0)} />
                  <div className="text-muted text-xs" style={{ marginTop: 3 }}>İki gönderim arası en az</div>
                </div>
                <div className="input-group">
                  <label>Günlük limit</label>
                  <input type="number" className="input" min="0" value={form.daily_limit} onChange={e => updForm('daily_limit', parseInt(e.target.value, 10) || 0)} />
                  <div className="text-muted text-xs" style={{ marginTop: 3 }}>0 = sınırsız</div>
                </div>
              </div>
            </div>

            <div className="keep-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10 }}>
              <button className="btn btn-ghost btn-lg" onClick={() => saveCampaign(false)} disabled={saving}><Ic n="save" s={18} />Taslak kaydet</button>
              <button className="btn btn-primary btn-lg" onClick={() => saveCampaign(true)} disabled={saving}><Ic n="play" s={16} />Kaydet ve başlat</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ═══ LİSTE ═══ */
  return (
    <div className="page-enter">
      <style>{CSS}</style>{toastEl}
      <PageHeader
        title="Retargeting"
        subtitle="Otomatik hedefli mesaj kampanyaları"
        actions={<button className="btn btn-primary" onClick={() => openCreate()} disabled={!selectedBot}><Ic n="plus" s={18} />Yeni kampanya</button>}
        botSelector={botSel}
      />

      <div className="stats-grid" style={{ marginBottom: 14 }}>
        <StatCard icon="target" label="Kampanya" value={fmtNum(totalStats.total)} />
        <StatCard icon="play" label="Aktif" value={fmtNum(totalStats.active)} color="var(--success)" bg="var(--success-s)" />
        <StatCard icon="send" label="Toplam gönderim" value={fmtNum(totalStats.sent)} color="var(--warning)" bg="var(--warning-s)" />
        <StatCard icon="alert" label="Başarısız" value={fmtNum(totalStats.failed)} color="var(--danger)" bg="var(--danger-s)" />
      </div>

      {loading ? (
        <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
      ) : !campaigns.length ? (
        <div className="card">
          <div className="empty-state">
            <div className="icon"><Ic n="target" s={48} /></div>
            <p style={{ fontWeight: 600, color: 'var(--text2)' }}>Henüz kampanya yok</p>
            <p className="text-sm" style={{ margin: '4px 0 14px' }}>İnaktif kullanıcıları geri kazanmak için ilk kampanyanı oluştur</p>
            <button className="btn btn-primary" onClick={() => openCreate()}><Ic n="plus" s={18} />Kampanya oluştur</button>
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {campaigns.map(camp => {
            const aud = audMap[camp.audience_type] || { ic: 'target', c: '#6ab3f3', label: camp.audience_type };
            const st = STATUS[camp.status] || STATUS.draft;
            return (
              <div key={camp.id} className="rt-row">
                <span style={{ position: 'absolute', left: 0, top: 10, bottom: 10, width: 3, borderRadius: '0 3px 3px 0', background: st.c }} />
                <div className="rt-ic" style={{ width: 44, height: 44, background: aud.c + '22', color: aud.c, cursor: 'pointer' }} onClick={() => loadDetail(camp)}><Ic n={aud.ic} s={22} /></div>
                <div className="rt-main" style={{ minWidth: 0, cursor: 'pointer' }} onClick={() => loadDetail(camp)}>
                  <div className="flex-center gap-8" style={{ minWidth: 0 }}>
                    <span className="truncate" style={{ fontWeight: 600, fontSize: 15 }}>{camp.name}</span>
                    <span className={`badge badge-${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="truncate text-muted" style={{ fontSize: 13 }}>
                    {aud.label} · {scheduleText(camp)}
                    {camp.next_run_at && camp.status === 'active' && <span style={{ color: 'var(--warning)' }}> · sonraki {new Date(camp.next_run_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                  </div>
                </div>
                <div className="rt-stats">
                  <div><b style={{ color: 'var(--link)' }}>{fmtNum(camp.sent_count)}</b><span>Gönderim</span></div>
                  <div><b style={{ color: 'var(--danger)' }}>{fmtNum(camp.failed_count)}</b><span>Hata</span></div>
                  <div><b style={{ color: 'var(--success)' }}>%{ctrOf(camp)}</b><span>CTR</span></div>
                </div>
                <div className="rt-acts">
                  <button className="btn btn-icon" title="Şimdi çalıştır" onClick={() => runNow(camp)} disabled={running === camp.id} style={{ color: 'var(--link)' }}>{running === camp.id ? <Spinner size={18} /> : <Ic n="send" s={18} />}</button>
                  <button className="btn btn-icon" title={camp.status === 'active' ? 'Duraklat' : 'Aktifleştir'} onClick={() => toggleStatus(camp)} style={{ color: camp.status === 'active' ? 'var(--warning)' : 'var(--success)' }}><Ic n={camp.status === 'active' ? 'pause' : 'play'} s={18} /></button>
                  <button className="btn btn-icon" title="Düzenle" onClick={() => openCreate(camp)}><Ic n="edit" s={18} /></button>
                  <button className="btn btn-icon" title="Sil" onClick={() => deleteCampaign(camp)} style={{ color: 'var(--danger)' }}><Ic n="trash" s={18} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
